import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, Link } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import { api } from '../api/client';
import {
  ChangeView,
  getMarkerIcon,
  getRiskLabel,
  formatPopupAddress,
} from '../components/MapHelpers';
import { StateSourceFull, StateSourceBadge } from '../components/StateSourceBadge';

// ─── Helpers ────────────────────────────────────────────────
const fmt = (v) => v || null;
const fmtDate = (d) => {
  if (!d) return null;
  try { return new Date(d).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' }); }
  catch { return d; }
};

const RISK_COLORS = {
  High: { bg: 'from-red-900/80 to-red-950/60', border: 'border-red-500/40', badge: 'bg-red-500', text: 'text-red-400', ring: 'ring-red-500/30' },
  Medium: { bg: 'from-amber-900/80 to-amber-950/60', border: 'border-amber-500/40', badge: 'bg-amber-500', text: 'text-amber-400', ring: 'ring-amber-500/30' },
  Low: { bg: 'from-emerald-900/80 to-emerald-950/60', border: 'border-emerald-500/40', badge: 'bg-emerald-500', text: 'text-emerald-400', ring: 'ring-emerald-500/30' },
  Unknown: { bg: 'from-blue-900/80 to-blue-950/60', border: 'border-blue-500/40', badge: 'bg-blue-500', text: 'text-blue-400', ring: 'ring-blue-500/30' },
};

// SVG icons (inline for zero deps)
const Icons = {
  back: <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18"/></svg>,
  link: <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/></svg>,
  alert: <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4.5c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z"/></svg>,
  map: <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/></svg>,
  calendar: <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>,
  shield: <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/></svg>,
  user: <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/></svg>,
  print: <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"/></svg>,
  share: <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"/></svg>,
  chevron: <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7"/></svg>,
};

// ─── Main Component ─────────────────────────────────────────
function OffenderDetailPage() {
  const { id } = useParams();
  const [offender, setOffender] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [nearbyOffenders, setNearbyOffenders] = useState([]);
  const [activeTab, setActiveTab] = useState(() => {
    const hash = window.location.hash.replace('#', '');
    return ['overview', 'offenses', 'addresses', 'details'].includes(hash) ? hash : 'overview';
  });
  const [satelliteView, setSatelliteView] = useState(false);
  const [imageError, setImageError] = useState(false);
  const nearbyRef = useRef(false);

  // Fetch offender
  useEffect(() => {
    setLoading(true);
    setError(null);
    setOffender(null);
    setNearbyOffenders([]);
    setImageError(false);
    nearbyRef.current = false;

    api.offenders.getById(id)
      .then(r => { setOffender(r.data); setLoading(false); })
      .catch(err => {
        setError(err.response?.data?.detail || err.message || 'Failed to load.');
        setLoading(false);
      });
  }, [id]);

  // Fetch nearby once
  useEffect(() => {
    if (!offender || !offender.latitude || !offender.longitude || nearbyRef.current) return;
    nearbyRef.current = true;

    api.offenders.nearby(offender.latitude, offender.longitude, 2, { exclude_incarcerated: 'true' })
      .then(r => {
        const list = (r.data.results || r.data || []).filter(o => o.id !== offender.id).slice(0, 12);
        setNearbyOffenders(list);
      })
      .catch(() => {});
  }, [offender]);

  // ─── Loading / Error States ─────────────────
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <div className="pr-spinner mx-auto mb-4" style={{ width: 48, height: 48 }}></div>
        <p className="text-slate-400 text-lg">Loading profile...</p>
      </div>
    </div>
  );

  if (error || !offender) return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <div className="text-center max-w-md">
        <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-4">
          {Icons.alert}
        </div>
        <h1 className="text-2xl font-bold text-white mb-2">{error ? 'Error' : 'Not Found'}</h1>
        <p className="text-slate-400 mb-6">{error || 'This offender record could not be found.'}</p>
        <Link to="/search" className="inline-flex items-center gap-2 px-5 py-2.5 bg-emerald-500 hover:bg-emerald-600 rounded-lg text-white font-medium transition-colors">
          {Icons.back} Back to Search
        </Link>
      </div>
    </div>
  );

  const risk = getRiskLabel(offender.risk_level, offender.tier_level);
  const colors = RISK_COLORS[risk] || RISK_COLORS.Unknown;
  const hasCoords = offender.latitude && offender.longitude;
  const convictions = offender.offense_details
    ? [...offender.offense_details].sort((a, b) => {
        const da = a.date_convicted ? new Date(a.date_convicted) : new Date(0);
        const db = b.date_convicted ? new Date(b.date_convicted) : new Date(0);
        return db - da;
      })
    : [];

  const addressesByType = {};
  (offender.detailed_addresses || []).forEach(a => {
    const t = a.address_type || 'Other';
    (addressesByType[t] = addressesByType[t] || []).push(a);
  });

  const physicalDetails = [
    { label: 'Age', value: fmt(offender.age) },
    { label: 'DOB', value: fmtDate(offender.date_of_birth) },
    { label: 'Sex', value: fmt(offender.sex) },
    { label: 'Race', value: fmt(offender.race) },
    { label: 'Height', value: fmt(offender.height) },
    { label: 'Weight', value: fmt(offender.weight) },
    { label: 'Hair', value: fmt(offender.hair_color) },
    { label: 'Eyes', value: fmt(offender.eye_color) },
  ].filter(d => d.value);

  const handleShare = async () => {
    const url = window.location.href;
    const text = `Sex Offender Alert: ${offender.full_name} - ${formatPopupAddress(offender)}`;
    if (navigator.share) {
      try { await navigator.share({ title: 'CommunityGuardAPI Alert', text, url }); } catch {}
    } else {
      navigator.clipboard?.writeText(url);
    }
  };

  const switchTab = (tabId) => {
    setActiveTab(tabId);
    window.history.replaceState(null, '', `#${tabId}`);
  };

  return (
    <div className="min-h-screen pb-12">
      <Helmet>
        <title>{offender.full_name} — Sex Offender Profile | CommunityGuardAPI</title>
        <meta name="description" content={`Registered sex offender profile for ${offender.full_name}. ${risk} risk. ${formatPopupAddress(offender)}.`} />
      </Helmet>

      {/* ═══ BREADCRUMB ═══ */}
      <div className="max-w-7xl mx-auto px-4 pt-4 pb-2">
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Link to="/" className="hover:text-slate-300 transition-colors">Home</Link>
          <span>/</span>
          <Link to="/search" className="hover:text-slate-300 transition-colors">Search</Link>
          <span>/</span>
          <span className="text-slate-300 truncate">{offender.full_name}</span>
        </div>
      </div>

      {/* ═══ HERO SECTION ═══ */}
      <div className={`bg-gradient-to-r ${colors.bg} border-b ${colors.border}`}>
        <div className="max-w-7xl mx-auto px-4 py-5 sm:py-8">
          <div className="flex gap-4 sm:gap-6 md:gap-8 items-start">

            {/* Photo */}
            <div className="relative flex-shrink-0">
              <div className={`w-24 h-24 sm:w-40 sm:h-40 md:w-48 md:h-48 rounded-xl sm:rounded-2xl overflow-hidden ring-2 sm:ring-4 ${colors.ring} shadow-2xl`}>
                {offender.offender_image_url && !imageError ? (
                  <img
                    src={offender.offender_image_url}
                    alt={offender.full_name}
                    className="w-full h-full object-cover"
                    onError={() => setImageError(true)}
                  />
                ) : (
                  <div className="w-full h-full bg-slate-700 flex items-center justify-center">
                    <svg className="w-12 h-12 sm:w-16 sm:h-16 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                  </div>
                )}
              </div>
              {/* Risk indicator dot */}
              <div className={`absolute -bottom-1 -right-1 w-6 h-6 sm:w-8 sm:h-8 ${colors.badge} rounded-full border-2 sm:border-4 border-slate-900 flex items-center justify-center`}>
                <span className="text-white text-[9px] sm:text-xs font-black">
                  {risk === 'High' ? '!' : risk === 'Medium' ? 'M' : risk === 'Low' ? 'L' : '?'}
                </span>
              </div>
            </div>

            {/* Info */}
            <div className="flex-grow min-w-0">
              <h1 className="text-xl sm:text-3xl md:text-4xl lg:text-5xl font-extrabold text-white tracking-tight mb-2 sm:mb-3 break-words">
                {offender.full_name}
              </h1>

              {/* Badges */}
              <div className="flex flex-wrap gap-1.5 sm:gap-2 mb-3 sm:mb-4">
                <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${colors.badge} text-white shadow-lg`}>
                  {Icons.shield} {risk} Risk
                </span>
                {offender.tier_level && (
                  <span className="px-3 py-1 rounded-full text-xs font-semibold bg-slate-700/80 text-slate-200 border border-slate-600">
                    {offender.tier_level}
                  </span>
                )}
                {offender.designation && (
                  <span className="px-3 py-1 rounded-full text-xs font-semibold bg-orange-600/80 text-orange-100">
                    {offender.designation}
                  </span>
                )}
                {offender.lifetime_registration && (
                  <span className="px-3 py-1 rounded-full text-xs font-semibold bg-red-800/80 text-red-200">
                    Lifetime Registration
                  </span>
                )}
                {offender.status && (
                  <span className="px-3 py-1 rounded-full text-xs font-semibold bg-slate-600/80 text-slate-200">
                    {offender.status}
                  </span>
                )}
              </div>

              {/* State source badge */}
              {offender.source_state && (
                <div className="mb-3 sm:mb-4">
                  <StateSourceFull stateCode={offender.source_state} />
                </div>
              )}

              {/* Quick details */}
              <div className="flex flex-wrap gap-x-3 sm:gap-x-6 gap-y-1 text-xs sm:text-sm text-slate-300 mb-3 sm:mb-5">
                {offender.registration_id && <span>ID: <strong className="text-white">{offender.registration_id}</strong></span>}
                {offender.source_state && <span>State: <strong className="text-white">{offender.source_state}</strong></span>}
                {offender.age && <span>Age: <strong className="text-white">{offender.age}</strong></span>}
                {offender.sex && <span>Sex: <strong className="text-white">{offender.sex}</strong></span>}
              </div>

              {/* Action buttons */}
              <div className="flex flex-wrap gap-2 sm:gap-3">
                {offender.tracking_url && (
                  <a href={offender.tracking_url} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 px-3 sm:px-4 py-1.5 sm:py-2 bg-emerald-500 hover:bg-emerald-600 rounded-lg text-white text-xs sm:text-sm font-semibold transition-colors shadow-lg">
                    {Icons.link} <span className="hidden sm:inline">State</span> Registry
                  </a>
                )}
                {offender.tip_submission_url && (
                  <a href={offender.tip_submission_url} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 px-3 sm:px-4 py-1.5 sm:py-2 bg-red-600 hover:bg-red-700 rounded-lg text-white text-xs sm:text-sm font-semibold transition-colors shadow-lg">
                    {Icons.alert} Tip
                  </a>
                )}
                <button onClick={handleShare}
                  className="inline-flex items-center gap-1.5 px-3 sm:px-4 py-1.5 sm:py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-white text-xs sm:text-sm font-medium transition-colors border border-slate-600">
                  {Icons.share} <span className="hidden sm:inline">Share</span>
                </button>
                <button onClick={() => window.print()}
                  className="hidden sm:inline-flex items-center gap-1.5 px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-white text-sm font-medium transition-colors border border-slate-600">
                  {Icons.print} Print
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ═══ PHYSICAL STATS BAR ═══ */}
      {physicalDetails.length > 0 && (
        <div className="bg-slate-800/50 border-b border-slate-700/50">
          <div className="max-w-7xl mx-auto px-4 py-3 sm:py-4">
            <div className="flex gap-4 sm:gap-6 md:gap-8 overflow-x-auto no-scrollbar sm:flex-wrap sm:justify-center">
              {physicalDetails.map(({ label, value }) => (
                <div key={label} className="text-center min-w-[55px] sm:min-w-[70px] flex-shrink-0">
                  <div className="text-[10px] sm:text-[11px] uppercase tracking-widest text-slate-500 mb-0.5">{label}</div>
                  <div className="text-xs sm:text-sm font-semibold text-white whitespace-nowrap">{value}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ═══ TAB NAVIGATION ═══ */}
      <div className="sticky top-16 z-30 bg-slate-900/95 backdrop-blur-sm border-b border-slate-700/50">
        <div className="max-w-7xl mx-auto px-2 sm:px-4">
          <div className="flex overflow-x-auto no-scrollbar -mx-2 px-2">
            {[
              { id: 'overview', label: 'Overview' },
              { id: 'offenses', label: `Offenses${convictions.length ? ` (${convictions.length})` : ''}` },
              { id: 'addresses', label: 'Addresses' },
              { id: 'details', label: 'Details' },
            ].map(tab => (
              <button
                key={tab.id}
                role="tab"
                aria-selected={activeTab === tab.id}
                onClick={() => switchTab(tab.id)}
                className={`px-3 sm:px-4 py-3 text-xs sm:text-sm font-medium whitespace-nowrap transition-colors border-b-2 flex-shrink-0 ${
                  activeTab === tab.id
                    ? 'border-emerald-500 text-emerald-400'
                    : 'border-transparent text-slate-400 hover:text-white hover:border-slate-500'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ═══ TAB CONTENT ═══ */}
      <div className="max-w-7xl mx-auto px-4 mt-6">

        {/* ─── OVERVIEW TAB ─── */}
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            {/* Map - 3 cols */}
            <div className="lg:col-span-3">
              {hasCoords ? (
                <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden shadow-xl">
                  <div className="flex items-center justify-between px-5 py-3 border-b border-slate-700">
                    <div className="flex items-center gap-2">
                      <span className={colors.text}>{Icons.map}</span>
                      <h2 className="font-heading font-semibold text-white">Registered Address</h2>
                    </div>
                    <button
                      onClick={() => setSatelliteView(!satelliteView)}
                      className="px-3 py-1 rounded text-xs font-medium transition-colors bg-slate-700 text-slate-300 hover:bg-slate-600 border border-slate-600"
                    >
                      {satelliteView ? 'Street' : 'Satellite'}
                    </button>
                  </div>
                  <div className="px-5 py-3 bg-slate-800/50">
                    <p className="text-slate-300 text-sm">{formatPopupAddress(offender)}</p>
                    {offender.address_type && <p className="text-xs text-slate-500 mt-0.5">Type: {offender.address_type}</p>}
                  </div>
                  <div className="h-72 md:h-96">
                    <MapContainer
                      center={[parseFloat(offender.latitude), parseFloat(offender.longitude)]}
                      zoom={15}
                      scrollWheelZoom={false}
                      style={{ height: '100%', width: '100%' }}
                    >
                      <ChangeView center={[parseFloat(offender.latitude), parseFloat(offender.longitude)]} zoom={15} />
                      <TileLayer
                        key={satelliteView ? 'sat' : 'street'}
                        attribution={satelliteView
                          ? '&copy; Esri, Maxar, Earthstar Geographics'
                          : '&copy; OpenStreetMap'}
                        url={satelliteView
                          ? 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'
                          : 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'}
                      />
                      <Marker
                        position={[parseFloat(offender.latitude), parseFloat(offender.longitude)]}
                        icon={getMarkerIcon(offender.risk_level, offender.tier_level)}
                      >
                        <Popup>
                          <strong>{offender.full_name}</strong><br/>{formatPopupAddress(offender)}
                        </Popup>
                      </Marker>
                    </MapContainer>
                  </div>
                </div>
              ) : (
                <div className="bg-slate-800 rounded-xl border border-slate-700 p-8 text-center">
                  <div className="text-slate-500 mb-3">{Icons.map}</div>
                  <p className="text-slate-400">No coordinates available for this offender.</p>
                  <p className="text-sm text-slate-500 mt-2">
                    Address: {formatPopupAddress(offender)}
                  </p>
                </div>
              )}

              {/* Offense summary */}
              {(convictions.length > 0 || offender.offense) && (
                <div className="mt-6 bg-slate-800 rounded-xl border border-slate-700 p-5 shadow-xl">
                  <h2 className="font-heading font-semibold text-white mb-3 flex items-center gap-2">
                    <span className="text-red-400">{Icons.alert}</span> Offense Summary
                  </h2>
                  {convictions.length > 0 ? (
                    <div className="space-y-3">
                      {convictions.slice(0, 3).map((c, i) => (
                        <div key={i} className="flex gap-3 items-start">
                          <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${colors.badge}`}></div>
                          <div>
                            <p className="text-sm text-white font-medium">{c.offense_description || 'Offense details not available'}</p>
                            <p className="text-xs text-slate-400">
                              {[fmtDate(c.date_convicted), c.conviction_state, c.court].filter(Boolean).join(' \u2022 ')}
                            </p>
                            {c.victim_age_details && (
                              <p className="text-xs text-orange-400 mt-0.5">Victim: {c.victim_age_details}</p>
                            )}
                          </div>
                        </div>
                      ))}
                      {convictions.length > 3 && (
                        <button onClick={() => switchTab('offenses')} className="text-sm text-emerald-400 hover:text-emerald-400/80 font-medium">
                          View all {convictions.length} convictions &rarr;
                        </button>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-300">{offender.offense}</p>
                  )}
                </div>
              )}
            </div>

            {/* Sidebar - 2 cols */}
            <div className="lg:col-span-2 space-y-6">
              {/* Registration card */}
              <SidebarCard title="Registration Details" icon={Icons.shield}>
                <InfoRow label="Risk Level" value={offender.risk_level} highlight={colors.text} />
                <InfoRow label="Tier" value={offender.tier_level} />
                <InfoRow label="Designation" value={offender.designation} />
                <InfoRow label="Lifetime" value={offender.lifetime_registration === true ? 'Yes' : offender.lifetime_registration === false ? 'No' : null} />
                <InfoRow label="Initial Reg." value={fmtDate(offender.initial_registration_date)} />
                <InfoRow label="Renewed" value={fmtDate(offender.registration_renewed_date)} />
                <InfoRow label="DOC ID" value={offender.department_of_corrections_id} />
              </SidebarCard>

              {/* Aliases */}
              {offender.aliases?.length > 0 && (
                <SidebarCard title="Known Aliases" icon={Icons.user}>
                  <div className="space-y-1.5">
                    {offender.aliases.map(a => (
                      <div key={a.id} className="flex items-center gap-2 text-sm">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 flex-shrink-0"></span>
                        <span className="text-slate-200">{[a.first_name, a.middle_name, a.last_name].filter(Boolean).join(' ')}</span>
                      </div>
                    ))}
                  </div>
                </SidebarCard>
              )}

              {/* Distinctive features */}
              {offender.distinctive_features?.length > 0 && (
                <SidebarCard title="Distinctive Features">
                  <div className="space-y-2">
                    {offender.distinctive_features.map(f => (
                      <div key={f.id} className="p-2.5 bg-slate-700/50 rounded-lg">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${
                            f.type?.toLowerCase() === 'tattoo' ? 'bg-purple-800/80 text-purple-200' :
                            f.type?.toLowerCase() === 'scar' ? 'bg-red-800/80 text-red-200' :
                            'bg-slate-600 text-slate-300'
                          }`}>{f.type || 'Feature'}</span>
                          {f.body_location && <span className="text-xs text-slate-400">{f.body_location}</span>}
                        </div>
                        <p className="text-xs text-slate-300">{f.description}</p>
                      </div>
                    ))}
                  </div>
                </SidebarCard>
              )}

              {/* Links */}
              <SidebarCard title="External Links">
                <div className="space-y-2">
                  {offender.tracking_url && (
                    <ExtLink href={offender.tracking_url} label="State Registry Profile" />
                  )}
                  {offender.tip_submission_url && (
                    <ExtLink href={offender.tip_submission_url} label="Submit a Tip" />
                  )}
                  {offender.map_link && (
                    <ExtLink href={offender.map_link} label="View on External Map" />
                  )}
                  {!offender.tracking_url && !offender.tip_submission_url && !offender.map_link && (
                    <p className="text-xs text-slate-500">No external links available.</p>
                  )}
                </div>
                <div className="mt-4 pt-3 border-t border-slate-700 space-y-1">
                  {offender.image_date && <p className="text-xs text-slate-500">Photo date: {fmtDate(offender.image_date)}</p>}
                  {offender.updated_at && <p className="text-xs text-slate-500">Last updated: {fmtDate(offender.updated_at)}</p>}
                </div>
              </SidebarCard>
            </div>
          </div>
        )}

        {/* ─── OFFENSES TAB ─── */}
        {activeTab === 'offenses' && (
          <div className="max-w-3xl">
            <h2 className="font-heading text-2xl font-bold text-white mb-6">Conviction History</h2>
            {convictions.length > 0 ? (
              <div className="pr-timeline">
                {convictions.map((c, i) => (
                  <div key={i} className="pr-timeline-item">
                    <div className="pr-timeline-dot"></div>
                    <div className="pr-timeline-content">
                      <div className="pr-timeline-date">{fmtDate(c.date_convicted) || 'Date unknown'}</div>
                      <h3 className="font-heading text-white font-semibold text-base mb-1.5">
                        {c.offense_description || 'Offense details not available'}
                      </h3>
                      <div className="text-sm text-slate-400 space-y-0.5">
                        {c.conviction_state && <p>State: {c.conviction_state}</p>}
                        {c.court && <p>Court: {c.court}</p>}
                        {c.court_case_number && <p>Case #: {c.court_case_number}</p>}
                        {c.victim_age_details && <p className="text-orange-400 font-medium">Victim: {c.victim_age_details}</p>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : offender.offense ? (
              <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                <p className="text-slate-300">{offender.offense}</p>
              </div>
            ) : (
              <div className="bg-slate-800 rounded-xl p-8 text-center border border-slate-700">
                <p className="text-slate-400">No detailed conviction records available for this offender.</p>
              </div>
            )}
          </div>
        )}

        {/* ─── ADDRESSES TAB ─── */}
        {activeTab === 'addresses' && (
          <div className="max-w-3xl">
            <h2 className="font-heading text-2xl font-bold text-white mb-6">Known Addresses</h2>

            {/* Primary address */}
            <div className="bg-slate-800 rounded-xl p-5 border border-slate-700 mb-4">
              <div className="flex items-center gap-2 mb-2">
                <span className="w-2.5 h-2.5 rounded-full bg-emerald-500"></span>
                <h3 className="font-heading font-semibold text-white text-sm">Primary Address</h3>
              </div>
              <p className="text-slate-300">{offender.address_line1 || 'Not available'}</p>
              <p className="text-slate-400 text-sm">{[offender.city, offender.state_id, offender.zip_code].filter(Boolean).join(', ')}</p>
              {offender.address_type && <p className="text-xs text-slate-500 mt-1">Type: {offender.address_type}</p>}
              {offender.address_source && <p className="text-xs text-slate-500">Source: {offender.address_source}</p>}
              {offender.address_received_date && <p className="text-xs text-slate-500">Received: {fmtDate(offender.address_received_date)}</p>}
            </div>

            {/* Detailed addresses */}
            {Object.entries(addressesByType).map(([type, addrs]) => (
              <div key={type} className="mb-4">
                <div className="flex items-center gap-2 mb-2 px-1">
                  <span className={`w-2.5 h-2.5 rounded-full ${
                    type.toLowerCase().includes('resid') ? 'bg-blue-400' :
                    type.toLowerCase().includes('work') || type.toLowerCase().includes('employ') ? 'bg-green-400' :
                    type.toLowerCase().includes('school') ? 'bg-yellow-400' : 'bg-slate-400'
                  }`}></span>
                  <h3 className="font-heading font-semibold text-white text-sm">{type}</h3>
                </div>
                {addrs.map(a => (
                  <div key={a.id} className="bg-slate-800 rounded-lg p-4 border border-slate-700 mb-2 ml-4">
                    <p className="text-slate-200 text-sm">{[a.street_line1, a.street_line2].filter(Boolean).join(', ')}</p>
                    <p className="text-slate-400 text-sm">{[a.city, a.state, a.zip_code].filter(Boolean).join(', ')}</p>
                  </div>
                ))}
              </div>
            ))}

            {Object.keys(addressesByType).length === 0 && (
              <p className="text-slate-500 text-sm mt-2">No additional address records available.</p>
            )}
          </div>
        )}

        {/* ─── DETAILS TAB ─── */}
        {activeTab === 'details' && (
          <div className="max-w-3xl">
            <h2 className="font-heading text-2xl font-bold text-white mb-6">Full Record Details</h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Physical */}
              <div className="bg-slate-800 rounded-xl p-5 border border-slate-700">
                <h3 className="font-heading font-semibold text-white mb-3">Physical Description</h3>
                <div className="space-y-2 text-sm">
                  {physicalDetails.map(({ label, value }) => (
                    <div key={label} className="flex justify-between">
                      <span className="text-slate-400">{label}</span>
                      <span className="text-white font-medium">{value}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Registration */}
              <div className="bg-slate-800 rounded-xl p-5 border border-slate-700">
                <h3 className="font-heading font-semibold text-white mb-3">Registration</h3>
                <div className="space-y-2 text-sm">
                  <DetailRow2 label="Registration ID" value={offender.registration_id} />
                  <DetailRow2 label="Risk Level" value={offender.risk_level} />
                  <DetailRow2 label="Tier Level" value={offender.tier_level} />
                  <DetailRow2 label="Designation" value={offender.designation} />
                  <DetailRow2 label="Status" value={offender.status} />
                  <DetailRow2 label="Lifetime" value={offender.lifetime_registration === true ? 'Yes' : offender.lifetime_registration === false ? 'No' : null} />
                  <DetailRow2 label="Initial Registration" value={fmtDate(offender.initial_registration_date)} />
                  <DetailRow2 label="Renewed" value={fmtDate(offender.registration_renewed_date)} />
                  <DetailRow2 label="DOC ID" value={offender.department_of_corrections_id} />
                  <DetailRow2 label="Source State" value={offender.source_state || offender.state_id} />
                </div>
              </div>
            </div>

            {/* Aliases */}
            {offender.aliases?.length > 0 && (
              <div className="bg-slate-800 rounded-xl p-5 border border-slate-700 mt-6">
                <h3 className="font-heading font-semibold text-white mb-3">Known Aliases</h3>
                <div className="flex flex-wrap gap-2">
                  {offender.aliases.map(a => (
                    <span key={a.id} className="px-3 py-1 bg-slate-700 rounded-full text-sm text-slate-200">
                      {[a.first_name, a.middle_name, a.last_name].filter(Boolean).join(' ')}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Features */}
            {offender.distinctive_features?.length > 0 && (
              <div className="bg-slate-800 rounded-xl p-5 border border-slate-700 mt-6">
                <h3 className="font-heading font-semibold text-white mb-3">Distinctive Features</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {offender.distinctive_features.map(f => (
                    <div key={f.id} className="p-3 bg-slate-700/50 rounded-lg text-sm">
                      <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${
                        f.type?.toLowerCase() === 'tattoo' ? 'bg-purple-800/80 text-purple-200' :
                        f.type?.toLowerCase() === 'scar' ? 'bg-red-800/80 text-red-200' :
                        'bg-slate-600 text-slate-300'
                      }`}>{f.type || 'Feature'}</span>
                      {f.body_location && <span className="text-xs text-slate-400 ml-2">{f.body_location}</span>}
                      <p className="text-slate-300 mt-1">{f.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Record metadata */}
            <div className="bg-slate-800 rounded-xl p-5 border border-slate-700 mt-6">
              <h3 className="font-heading font-semibold text-white mb-3">Record Metadata</h3>
              <div className="space-y-2 text-sm">
                <DetailRow2 label="Created" value={fmtDate(offender.created_at)} />
                <DetailRow2 label="Last Updated" value={fmtDate(offender.updated_at)} />
                <DetailRow2 label="Last Scraped" value={fmtDate(offender.last_updated)} />
                <DetailRow2 label="Photo Date" value={fmtDate(offender.image_date) || fmtDate(offender.photo_taken_date)} />
              </div>
            </div>
          </div>
        )}

      </div>

      {/* ═══ NEARBY OFFENDERS ═══ */}
      {nearbyOffenders.length > 0 && (
        <div className="max-w-7xl mx-auto px-4 mt-10">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-xl font-bold text-white">Nearby Offenders</h2>
            <span className="text-xs text-slate-500">Within 2 miles</span>
          </div>
          <div className="flex gap-4 overflow-x-auto pb-4 no-scrollbar">
            {nearbyOffenders.map(n => (
              <NearbyCard key={n.id} offender={n} />
            ))}
          </div>
        </div>
      )}

      {/* ═══ SAFETY NOTICE ═══ */}
      <div className="max-w-7xl mx-auto px-4 mt-10">
        <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-5">
          <div className="flex gap-3 items-start">
            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center mt-0.5">
              <span className="text-amber-400">{Icons.alert}</span>
            </div>
            <div>
              <h3 className="font-heading text-sm font-semibold text-white mb-1">Important Notice</h3>
              <p className="text-xs text-slate-400 leading-relaxed">
                This data is provided for informational purposes. Always verify through official sources. Using this information to harass,
                threaten, or intimidate any person is a criminal offense. Data is sourced from public sex offender registries
                and may not reflect current status.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ═══ BACK NAVIGATION ═══ */}
      <div className="max-w-7xl mx-auto px-4 mt-8 flex justify-center">
        <Link to="/search" className="inline-flex items-center gap-2 px-5 py-2.5 bg-slate-800 hover:bg-slate-700 rounded-lg text-white text-sm font-medium transition-colors border border-slate-700">
          {Icons.back} Back to Search
        </Link>
      </div>
    </div>
  );
}

// ─── Subcomponents ──────────────────────────────────────────
function SidebarCard({ title, icon, children }) {
  return (
    <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden shadow-xl">
      <div className="flex items-center gap-2 px-5 py-3 border-b border-slate-700 bg-slate-800/80">
        {icon && <span className="text-emerald-400">{icon}</span>}
        <h3 className="font-heading font-semibold text-white text-sm">{title}</h3>
      </div>
      <div className="px-5 py-4">{children}</div>
    </div>
  );
}

function InfoRow({ label, value, highlight }) {
  if (!value) return null;
  return (
    <div className="flex justify-between items-start py-1.5 border-b border-slate-700/50 last:border-0">
      <span className="text-xs text-slate-400">{label}</span>
      <span className={`text-xs font-medium text-right ml-3 ${highlight || 'text-white'}`}>{value}</span>
    </div>
  );
}

function DetailRow2({ label, value }) {
  return (
    <div className="flex justify-between items-start">
      <span className="text-slate-400">{label}</span>
      <span className="text-white font-medium text-right ml-3">{value || 'N/A'}</span>
    </div>
  );
}

function NearbyCard({ offender: n }) {
  const [imgErr, setImgErr] = useState(false);
  const nRisk = getRiskLabel(n.risk_level, n.tier_level);
  const nColors = RISK_COLORS[nRisk] || RISK_COLORS.Unknown;
  return (
    <Link
      to={`/offenders/${n.id}`}
      className="flex-shrink-0 w-36 bg-slate-800 rounded-xl border border-slate-700 hover:border-slate-500 transition-all p-3 text-center group"
    >
      <div className="w-16 h-16 rounded-full overflow-hidden mx-auto mb-2 ring-2 ring-slate-700 group-hover:ring-emerald-500 transition-all">
        {n.offender_image_url && !imgErr ? (
          <img src={n.offender_image_url} alt={n.full_name} className="w-full h-full object-cover"
            onError={() => setImgErr(true)} />
        ) : (
          <div className="w-full h-full bg-slate-700 flex items-center justify-center">
            <svg className="w-6 h-6 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
          </div>
        )}
      </div>
      <p className="text-xs font-semibold text-white truncate">{n.full_name}</p>
      <span className={`inline-block mt-1 text-[10px] font-bold px-2 py-0.5 rounded-full ${nColors.badge} text-white`}>
        {nRisk}
      </span>
      {n.source_state && (
        <div className="mt-1"><StateSourceBadge stateCode={n.source_state} /></div>
      )}
      {n.distance_miles != null && (
        <p className="text-[10px] text-slate-400 mt-1">{n.distance_miles} mi</p>
      )}
    </Link>
  );
}

function ExtLink({ href, label }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer"
      className="flex items-center justify-between p-2.5 bg-slate-700/50 rounded-lg hover:bg-slate-700 transition-colors group">
      <span className="text-sm text-emerald-400 group-hover:text-amber-400 transition-colors">{label}</span>
      <span className="text-slate-500 group-hover:text-emerald-400 transition-colors">{Icons.chevron}</span>
    </a>
  );
}

export default OffenderDetailPage;
